import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { supabase } from '@/lib/supabase';
import { buildApiUrl } from '@/utils/apiUrls';
import { normalizePhantomRestaurant } from '@/hooks/restaurant-discovery/useRestaurantsSource';
import { writePhantomRestaurantCache } from '@/hooks/restaurant-discovery/phantomRestaurantSnapshots';
import { appendStatusEntry, cartStatusLabel, toolStatusLabel } from './assistantThreadStatus';
import {
  markProposalsReported,
  settleInterruptedProposals,
  supersedeOpenProposals,
  unreportedProposalOutcomes,
} from './assistantProposals';
import { executeProposal } from '@/domains/assistant/proposalExecutors';
import { useSignedInContact } from '@/hooks/useSignedInContact';
import { ASSISTANT_CUT_OFF_MESSAGE, coachFacingErrorText, readAssistantStream } from './assistantStream';
import { clearPendingTurn, readPendingTurn, recoverInterruptedTurn, writePendingTurn } from './assistantPendingTurn';
import { buildAssistantRestaurantPath } from '@/utils/assistantRestaurantLink';
import { SIGNED_OUT_EVENT } from '@/utils/signOutStorage';

const AssistantContext = createContext(undefined);

// Discovery-shaped rows the assistant stream has delivered (card snapshots,
// navigate payloads), keyed by provider id. Written as ONE growing cache
// entry so the restaurant detail page hydrates exactly as it would from the
// discovery grid — rating, distance, hours, caps, hero image.
const assistantSnapshotRows = new Map();
if (typeof window !== 'undefined') {
  window.addEventListener(SIGNED_OUT_EVENT, () => assistantSnapshotRows.clear());
}

const cacheAssistantRestaurantRows = (rawRows) => {
  const rows = (Array.isArray(rawRows) ? rawRows : []).filter(
    (row) => row && typeof row === 'object',
  );
  if (rows.length === 0) return;
  try {
    for (const raw of rows) {
      const normalized = normalizePhantomRestaurant(raw);
      const key =
        normalized?.provider_restaurant_ids?.mcp || normalized?.api_id || normalized?.id;
      if (key) assistantSnapshotRows.set(String(key), normalized);
    }
    writePhantomRestaurantCache([...assistantSnapshotRows.values()], { query: 'assistant-chat' });
  } catch {
    // Non-fatal: the detail page falls back to its own DB/provider hydration.
  }
};

// --- Conversation persistence (per tab) -------------------------------------
// A hard refresh used to lose the chat: the thread lives in React state and
// the server transcript is keyed by a conversationId that also lived only in
// memory. Persist all three in sessionStorage (per-tab, so two tabs never
// fight over one conversation) and rehydrate on mount. If the server session
// is still alive the next message resumes with full tool context; otherwise
// the text-history fallback applies. Conversations older than a day are not
// restored — stale context only costs tokens.
const CONVERSATION_STORAGE_KEY = 'mealops:assistant-conversation:v1';
const CONVERSATION_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const CONVERSATION_MAX_THREAD_ENTRIES = 200;

const readStoredConversation = (userId) => {
  try {
    const raw = window.sessionStorage.getItem(CONVERSATION_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    if (!stored || stored.userId !== userId) return null;
    if (!stored.savedAt || Date.now() - stored.savedAt > CONVERSATION_MAX_AGE_MS) return null;
    if (!Array.isArray(stored.thread) || !Array.isArray(stored.history)) return null;
    return stored;
  } catch {
    return null;
  }
};

const writeStoredConversation = (payload) => {
  try {
    window.sessionStorage.setItem(CONVERSATION_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota/privacy-mode failures just mean no persistence.
  }
};

const clearStoredConversation = () => {
  try {
    window.sessionStorage.removeItem(CONVERSATION_STORAGE_KEY);
  } catch {
    // Ignore.
  }
};

const PAGE_LABELS = {
  '/dashboard-home': 'the Dashboard',
  '/calendar-order-scheduling': 'the Calendar',
  '/order-history-management': 'Order History',
  '/team-members-management': 'Team Management',
  '/home-restaurant-discovery': 'Restaurant Discovery',
  // Shown as "Opened Checkout" (it read "Opened the Cart").
  '/shopping-cart-checkout': 'Checkout',
  '/payment-methods-billing': 'Payment Methods',
  '/saves': 'Saves',
  '/polls': 'Polls',
  '/leaderboard': 'the Leaderboard',
  '/profile': 'your Profile',
};

// The coach is on the menu page of the restaurant this cart belongs to.
const isOnCartRestaurantPage = (cart) => {
  const currentPath = window.location.pathname;
  if (!currentPath.startsWith('/restaurant/')) return false;
  return Boolean(
    (cart?.restaurantUuid && currentPath.includes(cart.restaurantUuid)) ||
      (cart?.providerRestaurantId && currentPath.includes(cart.providerRestaurantId)),
  );
};

const getAccessToken = async () => {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
};

export const AssistantProvider = ({ children }) => {
  const { user, activeTeam } = useAuth();
  const navigate = useNavigate();
  // Who the driver reaches at a location the coach saves from a card: the
  // person adding it, as on the Saves page.
  const signedInContact = useSignedInContact();

  const [isOpen, setIsOpen] = useState(false);
  const [thread, setThread] = useState([]);
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const nextIdRef = useRef(0);
  // Server-side conversation continuation: the API keeps the FULL transcript
  // (tool calls included) under this id, so follow-ups reuse earlier lookups
  // instead of re-searching. The text history below stays as the fallback for
  // a server restart.
  const conversationIdRef = useRef(null);
  // A turn a page reload cut off: the next request tells the server, so the
  // model knows its last reply never reached the coach.
  const interruptedTurnRef = useRef(false);
  // The page is going away (a reload, a link out). The browser breaks the
  // reply's stream while this page's code still runs, and the send used to
  // read that as an ended turn and clear its pending marker, so the reloaded
  // page never knew (bigtest round 2, 2026-10-08).
  const pageLeavingRef = useRef(false);
  useEffect(() => {
    const leaving = () => {
      pageLeavingRef.current = true;
    };
    // Back from the back-forward cache: the page stayed after all.
    const shown = () => {
      pageLeavingRef.current = false;
    };
    window.addEventListener('beforeunload', leaving);
    window.addEventListener('pagehide', leaving);
    window.addEventListener('pageshow', shown);
    return () => {
      window.removeEventListener('beforeunload', leaving);
      window.removeEventListener('pagehide', leaving);
      window.removeEventListener('pageshow', shown);
    };
  }, []);

  // Reset the conversation when the signed-in user changes or logs out.
  const userId = user?.id || null;
  const prevUserIdRef = useRef(userId);
  useEffect(() => {
    if (prevUserIdRef.current !== userId) {
      // On a fresh load this fires once for auth resolving (null -> user);
      // only a switch BETWEEN users (or a logout) may wipe the stored
      // conversation, or rehydration below would never see it.
      const wasRealUser = prevUserIdRef.current !== null;
      prevUserIdRef.current = userId;
      if (wasRealUser) {
        setThread([]);
        setHistory([]);
        setSending(false);
        setIsOpen(false);
        conversationIdRef.current = null;
        interruptedTurnRef.current = false;
        clearStoredConversation();
        clearPendingTurn();
      }
    }
  }, [userId]);

  // Rehydrate this tab's conversation after a refresh (once the user is known).
  const rehydratedRef = useRef(false);
  useEffect(() => {
    if (rehydratedRef.current || !userId) return;
    rehydratedRef.current = true;
    const stored = readStoredConversation(userId);
    const pending = readPendingTurn(userId);
    clearPendingTurn();
    // A turn still marked pending was cut off by the reload (see
    // assistantPendingTurn.js), unless it belongs to another conversation.
    const interrupted =
      pending && (!stored?.conversationId || !pending.conversationId || stored.conversationId === pending.conversationId)
        ? pending
        : null;
    if (!stored && !interrupted) return;
    conversationIdRef.current = stored?.conversationId || interrupted?.conversationId || null;
    nextIdRef.current = stored?.nextId || (stored?.thread.length ?? 0) + 1;
    const recovered = recoverInterruptedTurn({
      thread: stored?.thread || [],
      history: stored?.history || [],
      pending: interrupted,
      makeId: () => {
        nextIdRef.current += 1;
        return `entry-${nextIdRef.current}`;
      },
    });
    interruptedTurnRef.current = Boolean(interrupted);
    setThread(settleInterruptedProposals(recovered.thread));
    setHistory(recovered.history);
  }, [userId]);

  // Persist on change (debounced — thread updates stream per token).
  useEffect(() => {
    if (!userId || thread.length === 0) return undefined;
    const timer = window.setTimeout(() => {
      writeStoredConversation({
        userId,
        conversationId: conversationIdRef.current,
        thread: thread.slice(-CONVERSATION_MAX_THREAD_ENTRIES),
        history,
        nextId: nextIdRef.current,
        savedAt: Date.now(),
      });
    }, 500);
    return () => window.clearTimeout(timer);
  }, [userId, thread, history]);

  // "New conversation": clears the thread and mints a fresh server session on
  // the next message. Also the cost lever — transcripts only ever grow, and a
  // finished order's context shouldn't ride along under tomorrow's question.
  const resetConversation = useCallback(() => {
    setThread([]);
    setHistory([]);
    conversationIdRef.current = null;
    interruptedTurnRef.current = false;
    clearStoredConversation();
    clearPendingTurn();
  }, []);

  const makeId = useCallback(() => {
    nextIdRef.current += 1;
    return `entry-${nextIdRef.current}`;
  }, []);

  const appendEntry = useCallback(
    (entry) => {
      const id = makeId();
      setThread((prev) => [...prev, { id, ...entry }]);
      return id;
    },
    [makeId],
  );

  // Tool-progress lines: consecutive repeats collapse ("Searching
  // restaurants… (3)") and a finished tool's line is replaced by the next
  // tool's rather than stacking up. See assistantThreadStatus.js.
  const appendToolStatus = useCallback(
    (label) => {
      const id = makeId();
      setThread((prev) => appendStatusEntry(prev, { id, type: 'status', label, transient: true }));
    },
    [makeId],
  );

  const updateEntry = useCallback((id, updater) => {
    setThread((prev) => prev.map((entry) => (entry.id === id ? updater(entry) : entry)));
  }, []);

  const openAssistant = useCallback(() => setIsOpen(true), []);
  const closeAssistant = useCallback(() => setIsOpen(false), []);
  const toggleAssistant = useCallback(() => setIsOpen((open) => !open), []);
  const goTo = useCallback((path) => navigate(path), [navigate]);

  const confirmQuote = useCallback(
    async (quotePayload, entryId = null) => {
      // The assistant already built a real DB cart (via add_items_to_cart), so
      // Confirm hands the coach off to the existing, fully-tested checkout page
      // loaded with that cart — reusing the whole authorize→submit→capture/void
      // money path (3DS, decline UX, split handling) instead of duplicating it.
      //
      // FUTURE: we may let the coach approve and place the order directly from
      // the chat conversation (an in-chat confirm endpoint that runs the same
      // checkout pipeline server-side). For now we route to checkout on purpose.
      const cartId = quotePayload?.cart_id || null;
      if (!cartId) {
        // A price check: the assistant priced an order without building a cart.
        appendEntry({
          type: 'status',
          icon: 'Info',
          label: 'This was a price check, so there is no cart to place yet. Ask me to build it as a cart first.',
        });
        return;
      }
      // The card outlives its cart: once ordered, there is nothing to place.
      try {
        const { data: cart } = await supabase.from('meal_carts').select('status').eq('id', cartId).maybeSingle();
        if (cart?.status === 'submitted') {
          if (entryId) updateEntry(entryId, (entry) => ({ ...entry, ordered: true }));
          appendEntry({
            type: 'status',
            icon: 'Info',
            label: 'This cart was already ordered, so there is nothing left to place. It is in Order History.',
          });
          return;
        }
      } catch {
        // Checkout itself refuses an ordered cart; go on.
      }
      appendEntry({ type: 'status', label: 'Taking you to checkout to place this order…' });
      setIsOpen(false);
      navigate(`/shopping-cart-checkout?cartId=${encodeURIComponent(cartId)}`);
    },
    [appendEntry, navigate, updateEntry],
  );

  // "View cart" on a cart status line: the drawer when the coach is on that
  // restaurant's menu page (the page's cart), otherwise the Saved Carts hub.
  const viewCart = useCallback((cart) => {
    window.dispatchEvent(new Event(isOnCartRestaurantPage(cart) ? 'openCartDrawer' : 'openCartHub'));
  }, []);

  const cancelQuote = useCallback(
    (entryId) => {
      updateEntry(entryId, (entry) => ({ ...entry, dismissed: true }));
    },
    [updateEntry],
  );

  // A confirmation card (a poll, a roster change, a saved location the
  // assistant proposed). Confirm runs the app's own code for the change; the
  // assistant hears what happened with the coach's next message.
  const threadRef = useRef(thread);
  threadRef.current = thread;
  const confirmProposal = useCallback(
    async (entryId, choice = null) => {
      const entry = threadRef.current.find((candidate) => candidate.id === entryId);
      if (!entry || entry.type !== 'proposal' || !['pending', 'failed'].includes(entry.state)) return;
      updateEntry(entryId, (current) => ({ ...current, state: 'working', error: null }));
      try {
        const result = await executeProposal(entry.proposal, { userId, contact: signedInContact, choice });
        updateEntry(entryId, (current) => ({
          ...current,
          state: 'done',
          reported: false,
          resultLabel: result?.label || 'Done',
          outcomeDetail: result?.detail || '',
          path: result?.path || null,
          pathLabel: result?.pathLabel || null,
        }));
      } catch (error) {
        const message = error?.message || 'That did not go through. Try again.';
        updateEntry(entryId, (current) => ({
          ...current,
          state: 'failed',
          reported: false,
          error: message,
          outcomeDetail: `The app said: ${message}`,
        }));
      }
    },
    [signedInContact, updateEntry, userId],
  );

  const cancelProposal = useCallback(
    (entryId) => {
      updateEntry(entryId, (entry) =>
        entry.type === 'proposal' && ['pending', 'failed'].includes(entry.state)
          ? { ...entry, state: 'cancelled', reported: false, error: null, outcomeDetail: '' }
          : entry,
      );
    },
    [updateEntry],
  );

  const sendMessage = useCallback(
    async (rawText) => {
      const text = String(rawText || '').trim();
      if (!text || sending) return;

      const nextHistory = [...history, { role: 'user', content: text }];
      setHistory(nextHistory);
      setSending(true);
      // What the coach did with confirmation cards since their last message
      // rides along, once: the assistant never sees the cards themselves.
      const proposalOutcomes = unreportedProposalOutcomes(threadRef.current);
      if (proposalOutcomes.length > 0) {
        setThread((prev) => markProposalsReported(prev, proposalOutcomes.map((outcome) => outcome.entryId)));
      }
      appendEntry({ type: 'user', text });

      let assistantEntryId = null;
      let bubbleText = '';
      let turnText = '';

      const conversationId =
        conversationIdRef.current ||
        (conversationIdRef.current = window.crypto?.randomUUID?.() || `conv-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      // Saved now, not with the debounced thread save: a reload before this
      // turn ends finds it (see assistantPendingTurn.js).
      writePendingTurn({ userId, conversationId, text });
      const interruptedTurn = interruptedTurnRef.current;
      interruptedTurnRef.current = false;
      // A coach sending is still here (a leave prompt they cancelled).
      pageLeavingRef.current = false;
      // The server ended the turn (a done or error event, or an error answer)
      // or at least started streaming it.
      let turnEnded = false;
      let streamStarted = false;

      try {
        const token = await getAccessToken();
        const response = await fetch(buildApiUrl('/api/assistant/chat'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            messages: nextHistory,
            teamId: activeTeam?.id || null,
            // "Tomorrow at 6" is on the coach's clock, not the server's.
            timeZone: (() => {
              try {
                return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
              } catch {
                return null;
              }
            })(),
            conversationId,
            ...(interruptedTurn ? { interruptedTurn: true } : {}),
            ...(proposalOutcomes.length > 0
              ? { proposalOutcomes: proposalOutcomes.map(({ entryId: _entryId, ...outcome }) => outcome) }
              : {}),
          }),
        });

        if (!response.ok || !response.body) {
          turnEnded = true;
          const json = await response.json().catch(() => null);
          const requestError = new Error(json?.error || '');
          requestError.status = response.status;
          throw requestError;
        }

        const handleEvent = (eventName, data) => {
          if (eventName === 'delta') {
            const deltaText = data?.text || '';
            bubbleText += deltaText;
            turnText += deltaText;
            if (!assistantEntryId) {
              assistantEntryId = makeId();
              const snapshot = bubbleText;
              setThread((prev) => [...prev, { id: assistantEntryId, type: 'assistant', text: snapshot }]);
            } else {
              const snapshot = bubbleText;
              updateEntry(assistantEntryId, (entry) => ({ ...entry, text: snapshot }));
            }
          } else if (eventName === 'tool') {
            appendToolStatus(toolStatusLabel(data?.name));
            // Any streamed text after this belongs to a new assistant bubble.
            assistantEntryId = null;
            bubbleText = '';
            if (turnText && !turnText.endsWith('\n')) turnText += '\n';
          } else if (eventName === 'navigate') {
            // Restaurant navigations carry the resolved fulfillment; rebuild
            // the link with the discovery URL builder so the page gets
            // lat/lng, asap and whenISO exactly like a discovery card.
            const path = buildAssistantRestaurantPath({
              restaurantId: data?.restaurantId,
              fulfillment: data?.fulfillment,
              path: data?.path,
            });
            if (data?.restaurant) cacheAssistantRestaurantRows([data.restaurant]);
            if (path.startsWith('/')) {
              const basePath = path.split('?')[0];
              const label = data?.label || PAGE_LABELS[basePath] || basePath;
              // Persistent link entry so the coach can come back to this page
              // later in the conversation.
              appendEntry({ type: 'navlink', path, label });
              navigate(path);
              // Text streamed after the navigation starts a new bubble.
              assistantEntryId = null;
              bubbleText = '';
              if (turnText && !turnText.endsWith('\n')) turnText += '\n';
            }
          } else if (eventName === 'cart') {
            const label = cartStatusLabel(data);
            const cart = {
              cartId: data?.cartId || null,
              restaurantUuid: data?.restaurantUuid || null,
              providerRestaurantId: data?.providerRestaurantId || null,
            };
            // A deleted cart has nothing to open: its View cart shows the hub.
            // The line takes the place of its tool's "Updating your cart…".
            const id = makeId();
            setThread((prev) =>
              appendStatusEntry(prev, { id, type: 'status', label, cart: data?.action === 'deleted' ? { cartId: null } : cart }),
            );
            // Refresh the cart surfaces, never pop them open: the hub and the
            // drawer render above the chat (z-[1200] vs z-50), so opening one
            // on every add/remove buried the conversation mid-turn. The status
            // line's "View cart" opens it when the coach wants to look.
            try {
              window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { cartId: cart.cartId } }));
              if (isOnCartRestaurantPage(cart)) window.dispatchEvent(new Event('assistantCartUpdated'));
            } catch {
              // Non-fatal — the cart still exists in the DB.
            }
          } else if (eventName === 'restaurants') {
            const restaurants = Array.isArray(data?.restaurants) ? data.restaurants : [];
            if (restaurants.length > 0) {
              cacheAssistantRestaurantRows(restaurants.map((card) => card?.snapshot));
              // Thread state keeps only the display fields; the snapshot's job
              // is done once it's in the shared cache.
              appendEntry({
                type: 'restaurants',
                restaurants: restaurants.map(({ snapshot: _snapshot, ...card }) => ({
                  ...card,
                  path: buildAssistantRestaurantPath({
                    restaurantId: card?.id,
                    fulfillment: data?.fulfillment,
                    path: card?.path,
                  }),
                })),
              });
              // Text streamed after the cards starts a new bubble.
              assistantEntryId = null;
              bubbleText = '';
              if (turnText && !turnText.endsWith('\n')) turnText += '\n';
            }
          } else if (eventName === 'quote') {
            appendEntry({ type: 'quote', quotePayload: data, dismissed: false });
          } else if (eventName === 'proposal') {
            if (data?.id && data?.kind) {
              // A corrected card takes the place of the open one it corrects.
              setThread((prev) => supersedeOpenProposals(prev, data));
              appendEntry({ type: 'proposal', proposal: data, state: 'pending' });
              // Text streamed after the card starts a new bubble.
              assistantEntryId = null;
              bubbleText = '';
              if (turnText && !turnText.endsWith('\n')) turnText += '\n';
            }
          } else if (eventName === 'error') {
            appendEntry({ type: 'error', label: coachFacingErrorText(data?.error) });
          }
        };

        streamStarted = true;
        const { terminal } = await readAssistantStream(response.body, handleEvent);
        turnEnded = Boolean(terminal);
        // No done/error event: the stream was cut off, not finished. A page
        // going away says so itself, after the reload.
        if (!terminal && !pageLeavingRef.current) appendEntry({ type: 'error', label: ASSISTANT_CUT_OFF_MESSAGE });
      } catch (error) {
        if (turnEnded || !pageLeavingRef.current) {
          appendEntry({ type: 'error', label: coachFacingErrorText(error?.message, { status: error?.status ?? null }) });
        }
      } finally {
        // Only a turn that ended is no longer pending. One cut off mid-stream
        // may still be finishing on the server, so its marker stays for a
        // reload to find (the next send replaces it); so does one whose
        // request broke because the page is going away.
        if (turnEnded || (!streamStarted && !pageLeavingRef.current)) clearPendingTurn();
        setSending(false);
        // Persist the assistant's streamed text into the API history so the
        // next turn carries the full conversation.
        const finalTurnText = turnText.trim();
        if (finalTurnText) {
          setHistory((prev) => [...prev, { role: 'assistant', content: finalTurnText }]);
        }
      }
    },
    [activeTeam?.id, appendEntry, appendToolStatus, history, makeId, navigate, sending, updateEntry, userId],
  );

  const value = useMemo(
    () => ({
      resetConversation,
      isOpen,
      openAssistant,
      closeAssistant,
      toggleAssistant,
      goTo,
      thread,
      sending,
      sendMessage,
      confirmQuote,
      cancelQuote,
      confirmProposal,
      cancelProposal,
      orgType: activeTeam?.org_type || null,
      viewCart,
    }),
    [resetConversation, isOpen, openAssistant, closeAssistant, toggleAssistant, goTo, thread, sending, sendMessage, confirmQuote, cancelQuote, confirmProposal, cancelProposal, activeTeam?.org_type, viewCart],
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
};

export const useAssistant = () => {
  const context = useContext(AssistantContext);
  if (context === undefined) {
    throw new Error('useAssistant must be used within an AssistantProvider');
  }
  return context;
};
